/**
 * Selectors for Viaja Ahorrando feature.
 * Provides access to discounts data from the Redux store.
 * All selectors read from state.search.viajaAhorrando (not a separate reducer).
 */

/**
 * Get current PNR being processed from search reducer (temporary).
 * This is the PNR from the URL query parameter.
 * @param {Object} state - Redux state
 * @returns {string|null} Current PNR code
 */
export const getCurrentPnr = (state) => {
  return state.search.getIn(['viajaAhorrando', 'currentPnr']) || null;
};

/**
 * Get vivaPnr from purchase meta (permanent storage).
 * This is the PNR stored in purchase.meta.vivaPnr when purchase is received.
 * Use this selector to check if Viaja Ahorrando is active for the current purchase.
 * @param {Object} state - Redux state
 * @returns {string|null} vivaPnr from purchase meta or null
 */
export const getVivaPnrFromPurchase = (state) => {
  const { purchase } = state;
  if (!purchase) return null;

  // Handle both Immutable Map and plain JS object
  const meta = purchase.get ? purchase.get('meta') : purchase.meta;
  if (!meta) return null;

  const vivaPnr = meta.get ? meta.get('vivaPnr') : meta.vivaPnr;
  return vivaPnr || null;
};

/**
 * Check if discounts are loading.
 * @param {Object} state - Redux state
 * @returns {boolean} True if discounts are loading
 */
export const isViajaAhorrandoLoading = (state) => {
  return state.search.getIn(['viajaAhorrando', 'isLoading']) || false;
};

/**
 * Check if discounts have been fetched.
 * @param {Object} state - Redux state
 * @returns {boolean} True if discounts have been fetched (successfully or with error)
 */
export const isViajaAhorrandoFetched = (state) => {
  return state.search.getIn(['viajaAhorrando', 'fetched']) || false;
};

/**
 * Check if Viaja Ahorrando discount is active and valid.
 * Use this selector to check if the discount is active and ready to be applied.
 * @param {Object} state - Redux state
 * @returns {boolean} True if Viaja Ahorrando is active
 */
export const isViajaAhorrandoActive = (state) => {
  const currentPnr = getCurrentPnr(state);
  const fetched = isViajaAhorrandoFetched(state);
  return Boolean(currentPnr && fetched);
};

/**
 * Get Viaja Ahorrando discounted trips from search reducer.
 * Trips are stored as a plain array in search.discountedTripsByCoupon['viajaAhorrando'].
 * Returns an empty array as default if no trips are found.
 * @param {Object} state - Redux state
 * @returns {Array} Array of discounted trip objects
 */
export const getViajaAhorrandoTrips = (state) => {
  if (!isViajaAhorrandoActive(state)) return [];
  return state.search.getIn(['discountedTripsByCoupon', 'viajaAhorrando'], []);
};

/**
 * Get Viaja Ahorrando discount for a specific trip.
 * Creates a map for O(1) lookup performance.
 * Returns an empty object as default if no trips are found.
 * @param {Object} state - Redux state
 * @returns {Object} Map of tripId -> viajaAhorrandoDiscount
 */
export const getViajaAhorrandoDiscountMap = (state) => {
  const trips = getViajaAhorrandoTrips(state);
  if (!trips || !trips.length) return {};

  // Handle both Immutable Maps and plain JS objects
  return trips.reduce((map, trip) => {
    // trip can be either an Immutable Map or a plain JS object
    const id = trip.get ? trip.get('id') : trip.id;
    const discount = trip.get ? trip.get('viajaAhorrandoDiscount') : trip.viajaAhorrandoDiscount;

    if (discount) {
      map[id] = discount;
    }
    return map;
  }, {});
};

/**
 * Get the count of Viaja Ahorrando passengers.
 * Useful for validating seat selection limits.
 * Returns passenger count when vivaPnr is active, otherwise 0.
 * @param {Object} state - Redux state
 * @returns {number} Number of passengers when Viaja Ahorrando is active, otherwise 0
 */
export const getViajaAhorrandoPassengerCount = (state) => {
  const vivaPnr = getVivaPnrFromPurchase(state);
  if (!vivaPnr) return 0;

  const passengers = state.purchase?.get?.('passengers');
  if (!passengers || passengers.isEmpty?.()) return 0;

  return passengers.size || 0;
};
